import { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useFetch } from 'hooks/useFetch'
import { useFormState } from 'hooks/useFormState'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import { useExameIdentificacaoNecropapiloscopicaService } from 'hooks/useExameIdentificacaoNecropapiloscopicaService'
import { useSnackBar } from 'hooks/useSnackBar'
import constants from 'utils/constants'
import { getTemplates } from './templates'

const schemaExame = {
  tipoDocumento: {
    presence: { allowEmpty: false, message: '^O tipos do documento é obrigatório' },
  },
  nome: {
    presence: { allowEmpty: false, message: '^O nome é obrigatório' },
  },
  numeroDocumento: {
    presence: { allowEmpty: false, message: '^O número do documento é obrigatório' },
  },
  resultado: {
    presence: { allowEmpty: false, message: '^O resultado da análise é obrigatório' },
  },
}

export const useEditorDocumento = (route, tipoExame) => {
  const { id } = useParams()
  const snackBar = useSnackBar()
  const fetch = useFetch()
  const exameRealizadoService = useExameRealizadoService()
  const exameNecropapiloscopicoService = useExameIdentificacaoNecropapiloscopicaService()
  
  const editorRef = useRef(null)
  
  const exameRealizado = useSelector((state) => state.editorExameRealizado.exameRealizadoEditorDocumento)
  const processandoEditor = useSelector((state) => state.editorExameRealizado.processandoEditor)
  
  const [documentoState, setDocumentoState] = useState('')
  const [fotosDocumentoState, setFotosDocumentoState] = useState([])
  const [alertaErroTipoImagem, setAlertaErroTipoImagem] = useState(false)
  const [openUpload, setOpenUpload] = useState(false)
  const [uploadedImages, setUploadedImages] = useState([])
  const [processandoUploadImagens, setProcessandoUploadImagens] = useState(false)
  const [processandoConfirmacaoImagens, setProcessandoConfirmacaoImagens] = useState(false)
  
  const formAnaliseNecropapiloscopica = useFormState(schemaExame)
  const [exibirFormExameNecropapiloscopico, setExibirFormExameNecropapiloscopico] = useState(false)
  const [exameNecropapiloscopico, setExameNecropapiloscopico] = useState(null)
  const [arrayFiliacao, setArrayFiliacao] = useState(['filiacao0', 'filiacao1'])
  const [processando, setProcessando] = useState(false)
  const [isFormularioInvalido, setIsFormularioInvalido] = useState(false)
  const [templates, setTemplates] = useState([])
  const [css, setCss] = useState(null)

  const getCSS = useCallback(async () => {
    try {
      const response = await fetch.get('/arquivo/css')
      setCss(response.data)
    } catch (err) {
      // Ignorar ou tratar erros silenciosamente
    }
  }, [])

  useEffect(() => {
    exameRealizadoService.listarExameRealizadoEditorGlobal(id)
    getCSS()
  }, [id, getCSS])

  useEffect(() => {
    setTemplates(getTemplates(tipoExame))
  }, [tipoExame])

  const fetchExameNecropapiloscopico = useCallback(async () => {
    try {
      const response = await exameNecropapiloscopicoService.listarExameNecropapiloscopicoDetalhadoAsync(id, setProcessando)
      if (response) {
        let novaDataString = `${response?.dataNascimento}T03:24:00`
        const novaData = new Date(novaDataString)
        const dados = {
          ...response,
          dataNascimento: novaData,
        }
        setExameNecropapiloscopico(dados)
      }
    } catch (err) {
      // Ignorar erros
    }
  }, [id])

  useEffect(() => {
    fetchExameNecropapiloscopico()
  }, [fetchExameNecropapiloscopico])

  useEffect(() => {
    if (route.errata) {
      setDocumentoState(constants.TEXTO_ERRATA_DEFAULT)
      if (exameRealizado?.tipo === 'NECROPAPILOSCOPIA') {
        setExibirFormExameNecropapiloscopico(true)
      } else {
        setExibirFormExameNecropapiloscopico(false)
      }
    } else if (exameRealizado) {
      setDocumentoState(exameRealizado.textoDocumento)
      setFotosDocumentoState(exameRealizado.arqs)
    }
  }, [exameRealizado, route.errata])

  useEffect(() => {
    let objectFiliacoes = {}
    if (exameNecropapiloscopico?.filiacoes?.length > 0) {
      let arrayFiliacoesDocumento = []
      exameNecropapiloscopico.filiacoes.forEach((filiacao, index) => {
        objectFiliacoes = {
          ...objectFiliacoes,
          [`filiacao${index}`]: filiacao,
        }
        arrayFiliacoesDocumento = arrayFiliacoesDocumento.concat(`filiacao${index}`)
      })
      setArrayFiliacao(arrayFiliacoesDocumento)
      formAnaliseNecropapiloscopica.setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          ...exameNecropapiloscopico,
          ...objectFiliacoes,
        },
      }))
    } else if (exameNecropapiloscopico) {
      formAnaliseNecropapiloscopica.setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          ...exameNecropapiloscopico,
        },
      }))
      setArrayFiliacao(['filiacao0', 'filiacao1'])
    }
  }, [exameNecropapiloscopico])

  useEffect(() => {
    if (
      exameRealizado?.tipo === 'NECROPAPILOSCOPIA' &&
      (formAnaliseNecropapiloscopica.hasErrorWithoutTouch('resultado') || 
       formAnaliseNecropapiloscopica.hasErrorWithoutTouch('tipoDocumento') || 
       formAnaliseNecropapiloscopica.hasErrorWithoutTouch('numeroDocumento') || 
       formAnaliseNecropapiloscopica.hasErrorWithoutTouch('nome'))
    ) {
      setIsFormularioInvalido(true)
    } else {
      setIsFormularioInvalido(false)
    }
  }, [formAnaliseNecropapiloscopica.formState.errors, exameRealizado?.tipo])

  const handleAdicionarFiliacao = () => {
    const newInput = `filiacao${arrayFiliacao.length}`
    const array = arrayFiliacao.concat([newInput])
    setArrayFiliacao(array)
  }

  const getResultadoLabel = (classificacao) => {
    switch (classificacao) {
      case 'IDENTIFICADO_DOCUMENTO':
        return 'Confronto com documento físico - Foram encontrados padrões datiloscópicos que confirmam a identidade do cadáver'
      case 'NAO_IDENTIFICADO_DOCUMENTO':
        return 'Confronto com documento físico - Não Foram encontrados padrões datiloscópicos que confirmam a identidade do cadáver'
      case 'IDENTIFICADO_SISTEMA':
        return 'Busca no sistema - Foram encontrados padrões datiloscópicos que confirmam a identidade do cadáver'
      case 'NAO_IDENTIFICADO_SISTEMA':
        return 'Busca no sistema - Não Foram encontrados padrões datiloscópicos que confirmam a identidade do cadáver'
      case 'NAO_IDENTIFICADO_FALTA_DOCUMENTO':
        return 'Não identificado por falta de documento'
      default:
        return ''
    }
  }

  const handleValorChange = (e) => {
    if (editorRef.current) {
      setDocumentoState(e.target.getContent())
    }
  }

  const gerarHTMLImagens = (imagens) => {
    let html = ``
    imagens.forEach((imagem) => {
      html += `
        <figure class="image">
          <img title="${imagem.id}" src="${process.env.REACT_APP_API_DNS}/arquivo/exame/open/${imagem.id}" style="width: 500px" />
          <figcaption>${imagem.caption}</figcaption>
        </figure>
        <p style="text-align: justify;">&nbsp;</p>
      `
    })
    return html
  }

  const inserirImagensNoCursor = () => {
    if (editorRef.current && window.tinymce) {
      const editor = window.tinymce.get(editorRef.current.id)
      if (editor) {
        const htmlImagens = gerarHTMLImagens(uploadedImages)
        editor.selection.setContent(htmlImagens)
      }
    }
  }

  const inserirImagensNoFinalDoDocumento = () => {
    if (editorRef.current && window.tinymce) {
      const editor = window.tinymce.get(editorRef.current.id)
      if (editor) {
        const htmlImagens = gerarHTMLImagens(uploadedImages)
        editor.setContent(`
          ${editor.getContent()} 
          ${htmlImagens}
        `)
      }
    }
  }

  const handleImageChange = useCallback(async (e) => {
    try {
      const formData = new FormData()
      const selectedFiles = Array.from(e.target.files)
      for (const image of selectedFiles) {
        formData.append('images', image)
      }

      setProcessandoUploadImagens(true)
      const response = await fetch.postOpen('/arquivo/exame/multiplas-fotos', formData)
      setProcessandoUploadImagens(false)
      setUploadedImages(
        response.data.map((image) => ({
          id: image.id,
          caption: '',
        }))
      )
    } catch (err) {
      setProcessandoUploadImagens(false)
    }
  }, [])

  const handleChangeLegenda = useCallback((value, index) => {
    setUploadedImages((prevState) => prevState.map((image, i) => (i === index ? { id: image.id, caption: value } : image)))
  }, [])

  const formatarNumero = (numero) => (parseInt(numero) < 10 ? '0' + parseInt(numero) : parseInt(numero))

  const gerarTrechoFotosETabelas = (fotos, tabelas) => {
    const ambos = fotos > 0 && tabelas > 0
    const nenhum = !(tabelas || fotos)

    if (nenhum) return '<span id="composicao"></span>'

    let tabelasTexto = ''
    let fotosTexto = ''
    if (fotos > 1) {
      fotosTexto = `${formatarNumero(fotos)} ilustrações fotográficas`
    } else {
      fotosTexto = `${formatarNumero(fotos)} ilustração fotográfica`
    }
    if (tabelas > 1) {
      tabelasTexto = `${formatarNumero(tabelas)} tabelas`
    } else {
      tabelasTexto = `${formatarNumero(tabelas)} tabela`
    }
    
    if (ambos) return `<span id="composicao">, contendo ${fotosTexto} e ${tabelasTexto},</span>`

    return fotos > 0 ? `<span id="composicao">, contendo ${fotosTexto}, </span>` : `<span id="composicao">, contendo ${tabelasTexto},</span>`
  }

  const updateFigcaption = useCallback((editor) => {
    if (!editor) return
    const figures = editor.dom.select('figure')
    const tables = editor.dom.select('table')
    let figIndex = 0
    figures.forEach((figure) => {
      let figcaption = figure.querySelector('figcaption')
      if (figcaption) {
        const captionElements = [];
        figIndex += 1
        const componentes = figcaption.textContent.split(' ')
        if (!["FOTO", "IMAGEM", "FIGURA"].includes(componentes[0].toUpperCase())) {
          captionElements.push("Foto", formatarNumero(figIndex), ...componentes);
        } else if (!isNaN(componentes[1] + 0)) {
          captionElements.push(componentes[0], formatarNumero(figIndex), ...componentes.slice(2));
        } else {
          captionElements.push(componentes[0], formatarNumero(figIndex), ...componentes.slice(1));
        }
        figcaption.innerHTML = captionElements.join(' ');
      }
    })

    const composicao = editor.dom.get('composicao')
    if (composicao) {
      composicao.innerHTML = gerarTrechoFotosETabelas(figIndex, tables.length)
    }
  }, [])

  const verificarLegenda = (imagens) => {
    let missingCaption = false;
    const _imagens = imagens.map((imagem) => {
      if (imagem.caption?.trim().length >= 3) return imagem;
      missingCaption = true;
      return { ...imagem, error: true };
    })
    return missingCaption ? _imagens : undefined;
  }

  const isExameExterna = (tipoExame) => ['ACHADO_CADAVER', 'IMOVEL', 'TRAFEGO', 'INCENDIO', 'ENGENHARIA_LEGAL', 'AMBIENTAL', 'TENTATIVA_HOMICIDIO'].includes(tipoExame)

  const handleConfirmUploadImagens = useCallback(async () => {
    try {
      setProcessandoConfirmacaoImagens(true)

      const imagensSemLegenda = verificarLegenda(uploadedImages);
      if (imagensSemLegenda) {
        setUploadedImages(imagensSemLegenda);
        setProcessandoConfirmacaoImagens(false);
        return;
      }

      const idsImagens = uploadedImages.map((image) => image.id)

      await exameRealizadoService.adicionarArquivosExame(exameRealizado.id, idsImagens)
      inserirImagensNoCursor()
      setUploadedImages([])
      
      if (editorRef.current && window.tinymce) {
        const editor = window.tinymce.get(editorRef.current.id)
        if (editor) {
          if (['EFICIENCIA_BALISTICA', 'IDENTIFICACAO_BALISTICA', 'CONFRONTO_BALISTICO', 'COINCIDENCIA_PERFIS_BALISTICO', 'ACHADO_CADAVER'].includes(exameRealizado.tipo)) {
            updateFigcaption(editor)
          } else if (isExameExterna(exameRealizado.tipo)) {
            updateFigcaption(editor)
          }
          setDocumentoState(editor.getContent())
        }
      }
      setProcessandoConfirmacaoImagens(false)
    } catch (err) {
      setProcessandoConfirmacaoImagens(false)
    }
  }, [uploadedImages, exameRealizado, updateFigcaption])

  const handleConfirmUploadImagensFinalDocumento = useCallback(async () => {
    try {
      setProcessandoConfirmacaoImagens(true)

      const imagensSemLegenda = verificarLegenda(uploadedImages);
      if (imagensSemLegenda) {
        setUploadedImages(imagensSemLegenda);
        setProcessandoConfirmacaoImagens(false);
        return;
      }

      const idsImagens = uploadedImages.map((image) => image.id)

      await exameRealizadoService.adicionarArquivosExame(exameRealizado.id, idsImagens)
      inserirImagensNoFinalDoDocumento()
      setUploadedImages([])

      if (editorRef.current && window.tinymce) {
        const editor = window.tinymce.get(editorRef.current.id)
        if (isExameExterna(exameRealizado.tipo)) {
          updateFigcaption(editor)
        }
        if (editor) {
          setDocumentoState(editor.getContent())
        }
      }
      setProcessandoConfirmacaoImagens(false)
    } catch (err) {
      setProcessandoConfirmacaoImagens(false)
    }
  }, [uploadedImages, exameRealizado])

  const handleCancelarUploadImagens = useCallback(() => {
    setUploadedImages([])
  }, [])

  const setDataColeta = (data) => {
    formAnaliseNecropapiloscopica.handleChangeCampoData('dataNascimento', data)
  }

  return {
    documentoState,
    setDocumentoState,
    fotosDocumentoState,
    alertaErroTipoImagem,
    setAlertaErroTipoImagem,
    openUpload,
    setOpenUpload,
    uploadedImages,
    processandoUploadImagens,
    processandoConfirmacaoImagens,
    formAnaliseNecropapiloscopica,
    exibirFormExameNecropapiloscopico,
    arrayFiliacao,
    isFormularioInvalido,
    css,
    templates,
    processandoEditor,
    exameRealizado,
    editorRef,
    handleValorChange,
    handleAdicionarFiliacao,
    getResultadoLabel,
    handleImageChange,
    handleChangeLegenda,
    handleConfirmUploadImagens,
    handleConfirmUploadImagensFinalDocumento,
    handleCancelarUploadImagens,
    setDataColeta,
    updateFigcaption,
    processando,
  }
}
